import { store } from './store';
import { SAVE_VERSION, type GameState } from './gameState';
import { migrateSave } from '../sim/migrate';

const PREFIX = 'sandyhollow.save';
const SLOT_KEY = 'sandyhollow.slot';
export const SLOTS = [1, 2, 3] as const;

export function getSlot(): number {
  try {
    const n = Number(localStorage.getItem(SLOT_KEY));
    return n === 1 || n === 2 || n === 3 ? n : 1;
  } catch {
    return 1;
  }
}
export function setSlot(n: number): void {
  try {
    localStorage.setItem(SLOT_KEY, String(n));
  } catch {
    /* ignore */
  }
}
const slotKey = (n: number = getSlot()): string => `${PREFIX}.${n}`;
const backupKey = (n: number = getSlot()): string => `${PREFIX}.${n}.bak`;

export function hasSave(slot: number = getSlot()): boolean {
  try {
    return localStorage.getItem(slotKey(slot)) != null;
  } catch {
    return false;
  }
}

export interface SlotSummary {
  slot: number;
  exists: boolean;
  day: number;
  farmName: string;
  money: number;
}

/** Peek at a slot's save (no migration/load) for the farm-select screen. */
export function slotSummary(n: number): SlotSummary {
  try {
    const raw = localStorage.getItem(slotKey(n));
    if (!raw) return { slot: n, exists: false, day: 0, farmName: '', money: 0 };
    const o = JSON.parse(raw) as { clock?: { day?: number }; farmName?: string; money?: number };
    return { slot: n, exists: true, day: o.clock?.day ?? 1, farmName: o.farmName ?? '', money: o.money ?? 0 };
  } catch {
    return { slot: n, exists: false, day: 0, farmName: '', money: 0 };
  }
}

export function saveGame(state: GameState = store.get()): boolean {
  try {
    state.saveVersion = SAVE_VERSION;
    const key = slotKey();
    // Roll the last good save into a backup before overwriting (insurance vs. a bad write).
    try {
      const prev = localStorage.getItem(key);
      if (prev) localStorage.setItem(backupKey(), prev);
    } catch {
      /* ignore */
    }
    localStorage.setItem(key, JSON.stringify(state));
    localStorage.setItem('sandyhollow.localAt', String(Date.now())); // for cloud newer-wins
    return true;
  } catch (e) {
    console.warn('[save] write failed', e);
    return false;
  }
}

function loadFromKey(key: string): GameState | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const result = migrateSave(JSON.parse(raw));
    if (!result.ok) {
      console.warn('[save] migration failed:', result.error);
      return null;
    }
    if (result.migratedFrom != null) console.info(`[save] migrated v${result.migratedFrom} → v${SAVE_VERSION}`);
    return result.state;
  } catch (e) {
    console.warn('[save] parse failed', e);
    return null;
  }
}

export function loadGame(slot: number = getSlot()): GameState | null {
  // Prefer the main save; fall back to the rolling backup if it's missing/corrupt.
  return loadFromKey(slotKey(slot)) ?? loadFromKey(backupKey(slot));
}

export function clearSave(slot: number = getSlot()): void {
  try {
    localStorage.removeItem(slotKey(slot));
  } catch {
    /* ignore */
  }
}

/** Serialize the current slot as a shareable/downloadable JSON file (iOS-friendly). */
export async function exportSave(state: GameState = store.get()): Promise<void> {
  const json = JSON.stringify(state, null, 2);
  const filename = `sandy-hollow-slot${getSlot()}-day${state.clock.day}.json`;
  const file = new File([json], filename, { type: 'application/json' });

  const canShareFiles = typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
  if (canShareFiles && typeof navigator.share === 'function') {
    try {
      await navigator.share({ files: [file], title: 'Sandy Hollow save' });
      return;
    } catch {
      // user cancelled or share failed — fall through to download
    }
  }

  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Read a user-picked JSON file, migrate it, and load it into the current slot. */
export async function importSaveFromFile(file: File): Promise<boolean> {
  try {
    const text = await file.text();
    const parsed: unknown = JSON.parse(text);
    const result = migrateSave(parsed);
    if (!result.ok) return false;
    store.replace(result.state);
    saveGame(result.state);
    return true;
  } catch {
    return false;
  }
}
